'use strict';

import AppData from 'Data/AppData';
import { get, post } from 'Data/SystemREST';
import { TransactionData } from 'Data/TransactionData';
import { CHARGE_ALLOCATION_STATUS, TRANSACTION_CATEGORY, TRANSACTION_TYPE } from 'Pages/Transactions/Enum';
import queryString from 'query-string';
import { downloadBlob } from 'Util';
import { getDateRangeForYear } from 'Util/dateUtil';
import GenericTableData from './GenericTableData';

// Charge Allocation Module Event Bus. Handles all interactions with Charge Allocations.
let ChargeAllocationData = GenericTableData.getTableDataBus(
  'CH_ALL',
  'charge_allocations',
);

ChargeAllocationData.updateStatuses = (caIds, nextStatus) => {
  if (!caIds?.length || !nextStatus) return;
  let updatedCharges = caIds.map((id) => ({
    id,
    ...(nextStatus === CHARGE_ALLOCATION_STATUS.CLEARED.key && {
      reconciled_by: AppData.userID(),
    }),
    status: nextStatus,
  }));

  ChargeAllocationData.updateOrCreateRecords(updatedCharges);
};

ChargeAllocationData.updateClearDates = (caIds, clearDate) => {
  if (!caIds?.length || !clearDate) return;
  let updatedCharges = caIds.map((id) => ({
    id,
    date_cleared: clearDate,
    reconciled_by: AppData.userID(),
    status: CHARGE_ALLOCATION_STATUS.CLEARED.key,
  }));

  ChargeAllocationData.updateOrCreateRecords(updatedCharges);
};

ChargeAllocationData.updatePostedDates = (caIds, postDate) => {
  if (!caIds?.length || !postDate) return;
  let updatedCharges = caIds.map((id) => ({
    id,
    date_posted: postDate,
  }));

  ChargeAllocationData.updateOrCreateRecords(updatedCharges);
};

ChargeAllocationData.getCAListFunction = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (fiscalYear?.length) {
      where['*date_posted'] = getDateRangeForYear(fiscalYear);
    }
    if (!where['*status']) {
      where['*status'] = [
        `!${CHARGE_ALLOCATION_STATUS.UNREVIEWED.key}`,
        `!${CHARGE_ALLOCATION_STATUS.EXCLUDED.key}`,
      ];
    }
    let options = {
      ...opts,
      where: where,
    };
    return ChargeAllocationData.getRecordList(options, true);
  };
};

ChargeAllocationData.getCAList = (opts) => {
  let where = {
    ...(opts?.where && { ...opts.where }),
  };
  if (!where['*status']) {
    where['*status'] = [
      `!${CHARGE_ALLOCATION_STATUS.UNREVIEWED.key}`,
      `!${CHARGE_ALLOCATION_STATUS.EXCLUDED.key}`,
    ];
  }
  let options = {
    ...opts,
    where: where,
  };
  return ChargeAllocationData.getRecordList(options, true);
};

ChargeAllocationData.getCAListForReview = (opts) => {
  let where = {
    ...(opts?.where && { ...opts.where }),
    '*status': CHARGE_ALLOCATION_STATUS.UNREVIEWED.key,
  };

  let options = {
    ...opts,
    where: where,
  };
  return ChargeAllocationData.getRecordList(options, true);
};

let exportCols = [
  { key: 'transaction.reference_number', header: 'Ref #' },
  { key: 'date_posted', header: 'Date Entered' },
  { key: 'charge_account.organization', header: 'Core Facility' },
  { key: 'transaction.vendor', header: 'Vendor' },
  { key: 'description', header: 'Description' },
  {
    key: 'category',
    header: 'Category',
    labelMap: Object.entries(TRANSACTION_CATEGORY).reduce(
      (mapObject, [key, value]) => {
        mapObject[key] = value;
        return mapObject;
      },
      {},
    ),
  },
  {
    key: 'transaction.transaction_type',
    header: 'Type',
    labelMap: Object.entries(TRANSACTION_TYPE).reduce(
      (mapObject, [key, value]) => {
        mapObject[key] = value;
        return mapObject;
      },
      {},
    ),
  },
  { key: 'charge_account.code', header: 'Act/Proj' },
  { key: 'expense_code.expense_code', header: 'Exp Code' },
  { key: 'amount', header: 'Amount' },
  { key: 'date_cleared', header: 'Date Cleared' },
  { key: 'reconciler', header: 'Reconciler' },
  {
    key: 'status',
    header: 'Status',
    labelMap: Object.values(CHARGE_ALLOCATION_STATUS).reduce(
      (mapObject, { key, label }) => {
        mapObject[key] = label;
        return mapObject;
      },
      {},
    ),
  },
];

ChargeAllocationData.getExportCAListFunction = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (fiscalYear?.length) {
      where['*date_posted'] = getDateRangeForYear(fiscalYear);
    }
    let columns = exportCols;

    let options = {
      ...opts,
      where: where,
      _include: '$charge_accounts.organizations,$transactions.vendors',
      columns,
    };

    return ChargeAllocationData.exportRecordList(options);
  };
};

ChargeAllocationData.saveChargesForTransaction = (
  chargeAllocationList,
  transaction,
) => {
  let purchaseTotal = chargeAllocationList.reduce((acc, charge) => {
    acc += +charge.amount;
    return acc;
  }, 0);
  return ChargeAllocationData.updateOrCreateRecords(
    chargeAllocationList,
    'CHARGES_SAVED_FOR_TRANSACTION',
  ).then(() => {
    if (parseFloat(transaction.purchase_total) !== parseFloat(purchaseTotal)) {
      TransactionData.updateRecord({
        id: transaction.id,
        purchase_total: purchaseTotal,
      });
    }
  });
};

ChargeAllocationData.getReportList = (reportOptions) => {
  let requestURL = `/reporter/details`;
  if (queryString.stringify(reportOptions).length > 0) {
    requestURL = `${requestURL}?${queryString.stringify(reportOptions)}`;
  }
  return get(requestURL)
    .send()
    .then(
      function (res) {
        let results = res.body;

        ChargeAllocationData.notifyListeners('CA_REPORT_LIST_LOADED', results);
        return results;
      },
      function (err) {
        console.log(err);
        ChargeAllocationData.notifyListeners('CA_REPORT_LIST_LOAD_ERROR', {
          message: `Unable to retrieve list`,
          debug: err,
        });
      },
    )
    .catch((err) => {
      console.log(err);
      ChargeAllocationData.notifyListeners('CA_REPORT_LIST_LOAD_ERROR', {
        message: `Unable to retrieve list`,
        debug: err,
      });
    });
};

ChargeAllocationData.exportReportAsCSV = (options, data) => {
  let requestURL = `/reporter/export`;
  let { fileName, ...otherOpts } = options;
  if (queryString.stringify(otherOpts).length > 0) {
    requestURL = `${requestURL}?${queryString.stringify(otherOpts)}`;
  }

  return post(requestURL)
    .send({ data })
    .then((res) => {
      if (res.statusCode === 200) {
        downloadBlob(res, `${fileName || 'Report'}.csv`);
        ChargeAllocationData.notifyListeners(
          'CA_REPORT_DATA_EXPORTED',
          res.text,
        );
        return Promise.resolve(res.text);
      } else if (res.statusCode === 204) {
        console.log('No Data to Download');
        return Promise.resolve(res);
      }
    })
    .catch((err) => {
      console.log(err);
      ChargeAllocationData.notifyListeners('EXPORT_REPORT_ERROR', {
        message: `Unable to export report data`,
        debug: err,
      });
    });
};

export default ChargeAllocationData;
